import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";

const LOGO_URL = "/images/logo.png";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const scrollTo = (id) => {
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(
                () =>
                    document
                        .getElementById(id)
                        ?.scrollIntoView({ behavior: "smooth" }),
                150
            );
        } else {
            document
                .getElementById(id)
                ?.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-[0_1px_0_rgba(7,15,29,0.03)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 h-28 sm:h-36 lg:h-40 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center group"
                >
                    <img
                        src={LOGO_URL}
                        alt="MechXpress"
                        className="h-20 sm:h-28 lg:h-[136px] w-auto rounded-xl transition-transform group-hover:scale-[1.04]"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
                    <button
                        data-testid="nav-how"
                        onClick={() => scrollTo("how-it-works")}
                        className="hover:text-[#070F1D] transition-colors"
                    >
                        How it works
                    </button>
                    <button
                        data-testid="nav-trust"
                        onClick={() => scrollTo("trust")}
                        className="hover:text-[#070F1D] transition-colors"
                    >
                        Trust
                    </button>
                    <button
                        data-testid="nav-faq"
                        onClick={() => scrollTo("faq")}
                        className="hover:text-[#070F1D] transition-colors"
                    >
                        FAQ
                    </button>
                    <Link
                        data-testid="nav-fixreplace"
                        to="/fix-or-replace"
                        className="hover:text-[#070F1D] transition-colors"
                    >
                        Fix or Replace?
                    </Link>
                    <Link
                        data-testid="nav-mechanics"
                        to="/for-mechanics"
                        className="hover:text-[#070F1D] transition-colors"
                    >
                        For Mechanics
                    </Link>
                </nav>

                <div className="hidden md:block">
                    <Button
                        data-testid="header-cta"
                        onClick={() => scrollTo("waitlist")}
                        className="bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full px-6 h-11 shadow-[0_8px_20px_-6px_rgba(16,185,129,0.5)] hover:shadow-[0_12px_30px_-8px_rgba(16,185,129,0.6)] transition-all"
                    >
                        Join Waitlist
                    </Button>
                </div>

                <button
                    data-testid="menu-toggle"
                    aria-label="Toggle menu"
                    className="md:hidden text-[#070F1D] p-2"
                    onClick={() => setOpen(!open)}
                >
                    {open ? (
                        <X className="w-6 h-6" />
                    ) : (
                        <Menu className="w-6 h-6" />
                    )}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl"
                >
                    <div className="px-6 py-4 flex flex-col gap-4 text-slate-600">
                        <button
                            onClick={() => scrollTo("how-it-works")}
                            className="text-left"
                        >
                            How it works
                        </button>
                        <button
                            onClick={() => scrollTo("trust")}
                            className="text-left"
                        >
                            Trust
                        </button>
                        <button
                            onClick={() => scrollTo("faq")}
                            className="text-left"
                        >
                            FAQ
                        </button>
                        <Link to="/fix-or-replace">Fix or Replace?</Link>
                        <Link to="/for-mechanics">For Mechanics</Link>
                        <Button
                            data-testid="mobile-cta"
                            onClick={() => scrollTo("waitlist")}
                            className="bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full"
                        >
                            Join Waitlist
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
}
